import { TypeAxis, NameFilesConfig } from '@/common/plotDefinitions.js';
import { getReportFiles } from '@/common/commonFunctions.js';
import { TYPE_PSSESSION,
         TYPE_2D,
         DATA_TYPE_DATA,
         BaseParser } from '@/common/sharedDefinitions/dataExtractors/baseExtractor.js';
import { FilesConfigKey } from '@/common/sharedDefinitions/commonPlotDefinitions'
import store from '@/store';

const notifyZone = 'Extract Data';

const reDT6Ext = RegExp('(.*)\\.dt6$', 'i');
const iconDT6 = 'dt6.png';


//Auxiliary functions
function getExtractedData(extractedArray, type) {
  if (TypeAxis[type].complex) {
    if (
      typeof extractedArray[type + '_im'] === 'undefined' ||
      extractedArray[type + '_im'].length !==
        extractedArray[type + '_re'].length
    ) {
      return extractedArray[type + '_re'];
    }
    const complexArray = [];
    for (let j = 0; j < extractedArray[type + '_re'].length; j++) {
      complexArray.push([
        extractedArray[type + '_re'][j],
        extractedArray[type + '_im'][j],
      ]);
    }
    return complexArray;
  } else {
    return extractedArray[type];
  }
}

class InBackEndParser extends BaseParser {
  static isBackEndExtraction(fileName) {
    return reDT6Ext.test(fileName);
  }

  async extract(f, parent) {
    const returnValue = this.getNewReturnValue();
    try {
      const fileName = f.name;
      const setRawFileResp = await parent.extractBinaryMiddleware({
        filename: fileName,
        file: f,
      });
      if (setRawFileResp) {
        const extractedData =
          typeof parent.getExtractedDataBinaryMiddleware
            .extracted_data !== 'undefined'
            ? parent.getExtractedDataBinaryMiddleware.extracted_data
            : [parent.getExtractedDataBinaryMiddleware];
        const dataExtract = this.getDataFromFileName(
          fileName,
          reDT6Ext,
        ); //Extract data from the file name
        const elementsData = [];
        for (let j = 0; j < extractedData.length; j++) {
          const newElement = {
            title: `${dataExtract.title}-${extractedData[j].title}`,
            group: dataExtract.group,
            concUnit: dataExtract.concUnit,
            X: extractedData[j].X,
            Y: extractedData[j].Y,
            date: extractedData[j].date || '',
            fileName: fileName,
          };
          if (typeof dataExtract.mapping !== 'undefined') {
            newElement.mapping = dataExtract.mapping;
          }
          if (typeof dataExtract.scan !== 'undefined') {
            newElement.scan = dataExtract.scan;
          }
          elementsData.push(newElement);
        }
        returnValue.processedData = elementsData;
        returnValue.lastError =
          returnValue.processedData.length === 0
            ? `File '${fileName}' is empty`
            : '';
        returnValue.dataType = DATA_TYPE_DATA;
        returnValue.type = TYPE_2D;
        returnValue.icon = iconDT6;
        returnValue.success = returnValue.processedData.length > 0;
      }
    } catch (error) {
      returnValue.lastError =
        'Error connecting with the server. Error: ' + error;
    }
    return returnValue;
  }
}

async function getDataFiles(report) {
  const reportFiles = await getReportFiles(report)
  return reportFiles.filter((f) => f.dataType === DATA_TYPE_DATA);
}

//Extract the data that will be sent to the backend depending on the axis-type
async function getDataFiles2D(parent, report, xType, yType, states, currentType = 'Forward_Current', scan = '') {
    if (!parent || !report) {
      throw new Error('Parent and report parameters are required');
      }
      if (typeof states === 'undefined') {
          states = [];
      }
      let dataFiles = [];
      let noReverseCurrCurvCount = 0;
      const dataTypeFiles = await getDataFiles(report);
      const map_verif = {};
      const map_duplicates = [];
      for (let j = 0; j < dataTypeFiles.length; j++) {
          const file = dataTypeFiles[j];
          if (file.type === TYPE_PSSESSION) {
              if (xType === '' || yType === '') {
                  const warnTitle = 'No supported';
                  const warnText = `Generic Study not supported with units-defined files '${file.name}' ignored`;
                  parent.$notifTerm.addNotification(
                      parent.$notifTerm.typesNotif.WARNING,
                      warnTitle,
                      warnText,
                      notifyZone,
                  );
                  continue;
              }
              for (let n = 0; n < file.extractedData.length; n++) {
                  if (typeof file.extractedData[n].enabled !== 'undefined' && !file.extractedData[n].enabled) {
                      //If the data is not enabled it is not included to the analysis
                      continue;
                  }
                  const newData = {
                      X: getExtractedData(file.extractedData[n], xType),
                      Y: getExtractedData(file.extractedData[n], yType),
                      title: file.extractedData[n].title,
                      fileName: file.extractedData[n].fileName,
                      group:typeof file.extractedData[n].group !== 'undefined' ? file.extractedData[n].group : 0,
                  };
                  // Add the optional properties if found
                  for (let property of ['state', 'concUnit', 'flipx', 'flipy', 'offsetx', 'offsety', 'scalex', 'scaley']) {
                      if (typeof file.extractedData[n][property] !=='undefined') {
                        newData[property] = file.extractedData[n][property];
                      }
                  }
                  //Verifies the reverse current
                  if ((yType === TypeAxis.current.id || xType === TypeAxis.current.id) && currentType !== 'Forward_Current') {
                      //If user requested reverse current and we use current in the study
                      if (typeof file.extractedData[n].reverseCurrent !== 'undefined') {
                          let currentVal = [...file.extractedData[n][TypeAxis.current.id]];
                          if (currentType === 'Reverse_Current') {
                            currentVal =file.extractedData[n].reverseCurrent;
                          } else if (currentType === 'Difference_Current') {
                              const currentValRev = file.extractedData[n].reverseCurrent; //Axiliar value for the sum
                              for (let a = 0; a < currentVal.length; a++) {
                                  currentVal[a] -= currentValRev[a];
                              }
                          }
                          if (yType === TypeAxis.current.id) {
                              //Set the new value to the dataline
                              newData.Y = currentVal;
                          } else {
                              newData.X = currentVal;
                          }
                      } else {
                          noReverseCurrCurvCount++;
                          continue;
                      }
                  }

                  if (scan !== '' && typeof file.extractedData[n].mapping !== 'undefined' && typeof file.extractedData[n].scan !== 'undefined') {
                      // Verify if a scan was selected
                      if (scan !== file.extractedData[n].scan) {
                          continue;
                      }
                      const arrMapping = file.extractedData[n].mapping;
                      newData.mapping = [];
                      for (let n = 0; n < arrMapping.length; n++) {
                          if (!isNaN(arrMapping[n]) && !isNaN(parseInt(arrMapping[n]))) {
                            newData.mapping.push(parseInt(arrMapping[n]));
                          }
                      }
                      //Verify the duplicate mapping elements
                      const join_mapping = `(${file.extractedData[n].mapping.join(',')})`;
                      if (map_verif[join_mapping] === true) {
                          map_duplicates.push(join_mapping);
                      } else {
                          map_verif[join_mapping] = true;
                      }
                  }
                  dataFiles.push(newData);
              }
          } else if (file.type === TYPE_2D) {
              const filterEnabled = file.extractedData.filter((data) => typeof(data.enabled) === 'undefined' || data.enabled);
              dataFiles.push(...filterEnabled);
          } else{
              const warnTitle = 'Unknown data type';
              const warnText = `The file category '${file.type}' isn't recognized. Please contact the administrator of the system`;
              parent.$notifTerm.addNotification(
                  parent.$notifTerm.typesNotif.WARNING,
                  warnTitle,
                  warnText,
                  notifyZone,
              );
          }
      }
      if (noReverseCurrCurvCount > 0) {
          const messageWarning = `${noReverseCurrCurvCount} elements does not have reverse current and were discarded. Please selected Forward Current in the configuration to get all the signals`;
          parent.$notify({
              group: 'AppGeneralUser',
              title: 'Extracting data',
              type: 'warn',
              text: messageWarning,
          });
          store.dispatch('addMessageTerminalSection',{
              type: 'WARN',
              text: `ExtractData.Get Data File. ${messageWarning}`,
          });
      }

      if(store.getters.getReportStatesListReportViewer.length > 1){
          // Consider the states. In this case default can mean empty, so we consider both
          if(states.includes('default') || states.includes('')){
              states.push(undefined);
          }
          dataFiles = dataFiles.filter((f) => states.includes(f.state));
          if(states.length === 0){
              const warnTitle = 'No states selected';
              const warnText = 'This cleared all the datalines';
              parent.$notifTerm.addNotification(
                  parent.$notifTerm.typesNotif.WARNING,
                  warnTitle,
                  warnText,
                  notifyZone,
              );
          }
      }

      //Sorting the elements according to the Proccessed Files
      if (
          typeof report.configuration[FilesConfigKey] !== 'undefined' &&
          typeof report.configuration[FilesConfigKey][NameFilesConfig.dataSort] !== 'undefined'
      ) {
          const dataSort = report.configuration[FilesConfigKey][NameFilesConfig.dataSort];
          if (dataSort.value === 'ascending') {
              dataFiles = dataFiles.sort((a, b) => { return a[dataSort.property] > b[dataSort.property] ? 1 : -1; });
          } else {
              dataFiles = dataFiles.sort((a, b) => { return a[dataSort.property] < b[dataSort.property] ? 1 : -1;});
          }
      }
      if (map_duplicates.length > 0) {
          const warnTitle = 'Duplicate mapping';
          const warnText = `The following mapping values are duplicated. Please verify in processed data and disable one element: ${map_duplicates.join(',')}`;
          parent.$notifTerm.addNotification(
              parent.$notifTerm.typesNotif.WARNING,
              warnTitle,
              warnText,
              'Data Extraction',
          );
      }

      return dataFiles;
}

export {
  InBackEndParser,
  getDataFiles,
  getDataFiles2D
}
